/**
 * The view: the camera, the sun's path drawn over it, and the controls
 * (Sprint 103.0, mockups 4 and 8). Without a camera the sky is drawn; without
 * an orientation sensor the view is dragged. Since Sprint 103.1 the compass is
 * steadied and turned to true north in `sensors/orientation.ts`, and the chip
 * opens the Compass panel (mockup 3) instead of tap-the-sun.
 */

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent,
  type ReactNode
} from 'react';
import { compassPoint, type LocalTime } from '@archisimple/solar';
import { degrees, wrapDegrees } from '../model/format.ts';
import { decimalYear, declinationDegrees } from '../model/declination.ts';
import {
  compassQuality,
  oneEuroAmount,
  shownError,
  steadyDegrees,
  type CompassQuality
} from '../model/heading.ts';
import { basisFromDevice, basisFromLook, PoseSmoother } from '../model/pose.ts';
import {
  coverFocal,
  project,
  toDirection,
  type CameraBasis,
  type SkyDirection,
  type Viewport
} from '../model/projection.ts';
import type { Day, HourMark, Place } from '../model/sky.ts';
import { startCamera, stopCamera } from '../sensors/camera.ts';
import { screenAngle, watchOrientation } from '../sensors/orientation.ts';
import { CompassSheet } from './CompassSheet.tsx';
import { Icon, LanguageSwitch, Notice, placeName, useWords } from './common.tsx';
import { draw, readPalette, type ReferenceLine, type Scene } from './overlay.ts';
import { SunStrip } from './SunStrip.tsx';

/** A phone's main camera, long side of a 4:3 sensor at 26 mm equivalent. */
const LENS_LONG_SIDE_DEGREES = 67;
const UI_INTERVAL_MS = 100;
/** `?debug` shows the compass alone beside the steadied heading, to tune the filter outdoors. */
const DEBUG = new URLSearchParams(globalThis.location?.search ?? '').has('debug');

type Status = 'pending' | 'on' | 'off';

interface Frame {
  readonly heading: number;
  readonly rawHeading?: number;
  readonly viewport: Viewport;
  readonly sun?: {
    readonly x: number;
    readonly y: number;
    readonly onScreen: boolean;
    readonly side: 'left' | 'right';
  };
  readonly rise?: {
    readonly onScreen: boolean;
    readonly side: 'left' | 'right';
    readonly y: number;
  };
  readonly set?: {
    readonly onScreen: boolean;
    readonly side: 'left' | 'right';
    readonly y: number;
  };
}

export interface ArProps {
  readonly place: Place;
  readonly day: Day;
  readonly hours: readonly HourMark[];
  readonly references: readonly ReferenceLine[];
  readonly sun: SkyDirection & { readonly aboveHorizon: boolean };
  readonly localTime: LocalTime;
  readonly dateLabel: string;
  readonly live: boolean;
  readonly playing: boolean;
  readonly wantCamera: boolean;
  readonly wantMotion: boolean;
  readonly sheet: ReactNode;
  readonly onTime: (time: LocalTime) => void;
  readonly onPlay: () => void;
  readonly onNow: () => void;
  readonly onOpenDate: () => void;
  readonly onSummary: () => void;
  readonly onChangePlace: () => void;
}

export function ArScreen(props: ArProps): ReactNode {
  const { t, language } = useWords();
  const root = useRef<HTMLDivElement>(null);
  const video = useRef<HTMLVideoElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [camera, setCamera] = useState<Status>(props.wantCamera ? 'pending' : 'off');
  const [motion, setMotion] = useState<Status>(props.wantMotion ? 'pending' : 'off');
  const [error, setError] = useState<number | undefined>(undefined);
  const [compassOpen, setCompassOpen] = useState(false);
  const [dismissed, setDismissed] = useState<ReadonlySet<'camera' | 'sensors' | 'compass'>>(
    new Set()
  );
  const dismiss = (notice: 'camera' | 'sensors' | 'compass') => (): void =>
    setDismissed((current) => new Set(current).add(notice));
  const [frame, setFrame] = useState<Frame | undefined>(undefined);
  const device = useRef<CameraBasis | undefined>(undefined);
  const rawHeading = useRef<number | undefined>(undefined);
  const shownHeading = useRef<number | undefined>(undefined);
  const look = useRef({ yaw: props.sun.aboveHorizon ? props.sun.azimuthDegrees : 180, pitch: 15 });
  const drag = useRef<{ x: number; y: number } | undefined>(undefined);
  const latest = useRef(props);
  const motionRef = useRef(motion);
  const cameraRef = useRef(camera);

  useEffect(() => {
    latest.current = props;
    motionRef.current = motion;
    cameraRef.current = camera;
  });

  useEffect(() => {
    if (!props.wantCamera || video.current === null) return;
    let stream: MediaStream | undefined;
    let cancelled = false;
    void startCamera(video.current).then((started) => {
      if (cancelled) {
        stopCamera(started);
        return;
      }
      stream = started;
      setCamera(started === undefined ? 'off' : 'on');
    });
    return () => {
      cancelled = true;
      stopCamera(stream);
    };
  }, [props.wantCamera]);

  // The place's declination, for this year: read at each reading through the ref.
  const [year] = useState(() => decimalYear(Date.now()));
  const declination = useMemo(
    () => declinationDegrees(props.place.latitude, props.place.longitude, year),
    [props.place.latitude, props.place.longitude, year]
  );
  const declinationRef = useRef(declination);
  useEffect(() => {
    declinationRef.current = declination;
  }, [declination]);

  useEffect(() => {
    if (!props.wantMotion) return;
    const smoother = new PoseSmoother((dt, speed) => oneEuroAmount(dt, speed));
    let lastError = 0;
    return watchOrientation(
      (reading) => {
        device.current = smoother.next(basisFromDevice(reading, screenAngle()), performance.now());
        rawHeading.current = reading.rawHeading;
        setMotion('on');
        // Re-rendered only when the shown error would change, not at every reading.
        const next =
          reading.errorDegrees === undefined ? undefined : Math.round(reading.errorDegrees);
        if (next !== lastError) {
          lastError = next ?? 0;
          setError(next);
        }
      },
      () => setMotion('off'),
      () => declinationRef.current
    );
  }, [props.wantMotion]);

  useEffect(() => {
    let handle = 0;
    let lastUi = 0;
    const step = (now: number): void => {
      handle = requestAnimationFrame(step);
      const element = root.current;
      const target = canvas.current;
      if (element === null || target === null) return;
      const viewport = viewportOf(element, video.current, cameraRef.current === 'on');
      const ratio = globalThis.devicePixelRatio || 1;
      if (target.width !== Math.round(viewport.width * ratio))
        target.width = Math.round(viewport.width * ratio);
      if (target.height !== Math.round(viewport.height * ratio))
        target.height = Math.round(viewport.height * ratio);
      const ctx = target.getContext('2d');
      if (ctx === null) return;
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
      const p = latest.current;
      const basis = baseBasis(motionRef.current, device.current, look.current);
      const scene: Scene = {
        camera: basis,
        viewport,
        drawnSky: cameraRef.current !== 'on',
        path: p.day.solar.path,
        hours: p.hours.map((h) => ({ ...h, label: tFor(h.hour) })),
        references: p.references,
        sun: p.sun,
        cardinals: CARDINALS.map((c) => ({ azimuth: c.azimuth, label: cardinalLabel(c.key) }))
      };
      draw(ctx, scene, readPalette(element));
      if (now - lastUi > UI_INTERVAL_MS) {
        lastUi = now;
        const next = frameOf(basis, viewport, p);
        shownHeading.current = steadyDegrees(shownHeading.current, next.heading);
        setFrame({
          ...next,
          heading: shownHeading.current,
          ...(rawHeading.current === undefined ? {} : { rawHeading: rawHeading.current })
        });
      }
    };
    const tFor = (hour: number): string => t('ar.hour', { hour });
    const cardinalLabel = (key: 'n' | 'e' | 's' | 'w'): string => t(`cardinal.${key}`);
    handle = requestAnimationFrame(step);
    return () => cancelAnimationFrame(handle);
  }, [t]);

  const onPointerDown = (event: PointerEvent): void => {
    if (motion !== 'on') drag.current = { x: event.clientX, y: event.clientY };
  };
  const onPointerMove = (event: PointerEvent): void => {
    const from = drag.current;
    if (from === undefined || frame === undefined) return;
    const perPixel = 180 / Math.PI / frame.viewport.focal;
    look.current = {
      yaw: wrapDegrees(look.current.yaw - (event.clientX - from.x) * perPixel),
      pitch: Math.max(-30, Math.min(85, look.current.pitch + (event.clientY - from.y) * perPixel))
    };
    drag.current = { x: event.clientX, y: event.clientY };
  };
  const onPointerUp = (): void => {
    drag.current = undefined;
  };

  const sunWords = t('ar.directionValue', {
    degrees: degrees(props.sun.azimuthDegrees, language),
    point: t(`point.${compassPoint(props.sun.azimuthDegrees)}`)
  });
  const height = t(props.sun.altitudeDegrees >= 0 ? 'ar.above' : 'ar.below', {
    degrees: degrees(Math.abs(props.sun.altitudeDegrees), language, 1)
  });
  const placeWords =
    props.place.source === 'device' || props.place.label !== undefined
      ? placeName(props.place, t)
      : `${degrees(props.place.latitude, language, 4)}°, ${degrees(props.place.longitude, language, 4)}°`;
  const quality = compassQuality(error);
  const rise = props.day.solar.sunrise;
  const set = props.day.solar.sunset;
  const first = props.day.solar.path[0];
  const last = props.day.solar.path.at(-1);

  return (
    <div
      ref={root}
      className="ar"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={() => (drag.current = undefined)}
    >
      <video
        ref={video}
        className="ar__video"
        playsInline
        muted
        autoPlay
        hidden={camera !== 'on'}
      />
      <canvas ref={canvas} className="ar__canvas" role="img" aria-label={t('ar.view')} />

      {frame?.sun !== undefined && !frame.sun.onScreen && props.sun.aboveHorizon ? (
        <span className="edge" style={edgeStyle(frame.sun.side, frame.sun.y)}>
          {frame.sun.side === 'left' ? `← ${t('ar.sun')}` : `${t('ar.sun')} →`}
        </span>
      ) : null}
      {frame?.rise !== undefined &&
      !frame.rise.onScreen &&
      rise.kind === 'at' &&
      first !== undefined ? (
        <span className="edge" style={edgeStyle(frame.rise.side, frame.rise.y)}>
          {edgeText(
            frame.rise.side,
            t('ar.sunrise', {
              time: rise.localTime,
              degrees: degrees(first.azimuthDegrees, language)
            })
          )}
        </span>
      ) : null}
      {frame?.set !== undefined &&
      !frame.set.onScreen &&
      set.kind === 'at' &&
      last !== undefined ? (
        <span className="edge" style={edgeStyle(frame.set.side, frame.set.y)}>
          {edgeText(
            frame.set.side,
            t('ar.sunset', { time: set.localTime, degrees: degrees(last.azimuthDegrees, language) })
          )}
        </span>
      ) : null}

      <div className="ar__top">
        <div className="row">
          <button
            type="button"
            className="btn place"
            onClick={props.onChangePlace}
            aria-label={t('place.change')}
          >
            <Icon name="pin" size={18} />
            <span>{placeWords}</span>
            {props.place.accuracyMetres === undefined ? null : (
              <small>
                {t('place.accuracy', { metres: Math.round(props.place.accuracyMetres) })}
              </small>
            )}
          </button>
          <button
            type="button"
            className="btn btn--round"
            onClick={props.onSummary}
            aria-label={t('ar.summary')}
          >
            <Icon name="info" />
          </button>
          <LanguageSwitch />
        </div>
        {frame === undefined ? null : <Tape heading={frame.heading} />}
        {DEBUG && frame !== undefined ? (
          <p className="debug">
            {`steady ${frame.heading}° · compass ${frame.rawHeading === undefined ? '–' : Math.round(frame.rawHeading)}° · ±${error ?? '–'}° · D ${declination.toFixed(1)}°`}
          </p>
        ) : null}
        {motion === 'on' ? (
          <button
            type="button"
            className={chipClass(quality)}
            onClick={() => setCompassOpen(true)}
            aria-haspopup="dialog"
          >
            <Icon name="compass" size={14} />
            {chipText(quality, error, t)}
          </button>
        ) : null}
        {camera === 'off' && props.wantCamera && !dismissed.has('camera') ? (
          <Notice title={t('fallback.camera.title')} onDismiss={dismiss('camera')}>
            {t('fallback.camera.body')}
          </Notice>
        ) : null}
        {motion === 'off' && !dismissed.has('sensors') ? (
          <Notice title={t('fallback.sensors.title')} onDismiss={dismiss('sensors')}>
            {t('fallback.sensors.body')}
          </Notice>
        ) : null}
        {/* Once the sensors notice is closed, this short line keeps the gesture known. */}
        {motion === 'off' && dismissed.has('sensors') ? (
          <p className="drag-hint">
            <Icon name="drag" size={18} />
            {t('fallback.drag')}
          </p>
        ) : null}
        {quality === 'poor' && !compassOpen && !dismissed.has('compass') ? (
          <Notice
            tone="danger"
            title={t('fallback.compass.title')}
            action={{ label: t('compass.open'), onClick: () => setCompassOpen(true) }}
            onDismiss={dismiss('compass')}
          >
            {t('fallback.compass.body')}
          </Notice>
        ) : null}
      </div>

      {props.sheet !== null ? (
        props.sheet
      ) : compassOpen ? (
        <CompassSheet
          quality={quality}
          errorDegrees={error}
          declinationDegrees={declination}
          onClose={() => setCompassOpen(false)}
        />
      ) : (
        <div className="ar__bottom">
          <div className="card card--float readout">
            <div>
              <span>{t('ar.direction')}</span>
              <strong>{sunWords}</strong>
            </div>
            <div>
              <span>{t('ar.height')}</span>
              <strong>{height}</strong>
            </div>
          </div>
          <div className="card card--float bar">
            <button
              type="button"
              className="btn btn--date"
              onClick={props.onOpenDate}
              aria-label={t('date.open')}
            >
              <Icon name="calendar" size={18} />
              {props.dateLabel}
            </button>
            <div className="spacer" />
            <button type="button" className="btn" aria-pressed={props.live} onClick={props.onNow}>
              {t('ar.now')}
            </button>
          </div>
          <SunStrip
            day={props.day}
            localTime={props.localTime}
            playing={props.playing}
            onTime={props.onTime}
            onPlay={props.onPlay}
          />
        </div>
      )}
    </div>
  );
}

const CARDINALS = [
  { key: 'n', azimuth: 0 },
  { key: 'e', azimuth: 90 },
  { key: 's', azimuth: 180 },
  { key: 'w', azimuth: 270 }
] as const;

function Tape({ heading }: { readonly heading: number }): ReactNode {
  const { t, language } = useWords();
  const span = 48;
  const ticks: { at: number; major: boolean; text?: string }[] = [];
  const start = Math.ceil((heading - span / 2) / 2) * 2;
  for (let a = start; a <= heading + span / 2; a += 2) {
    const azimuth = wrapDegrees(a);
    const major = azimuth % 10 === 0;
    const cardinal = CARDINALS.find((c) => c.azimuth === azimuth);
    ticks.push({
      at: 50 + ((a - heading) / span) * 100,
      major,
      ...(major
        ? { text: cardinal === undefined ? `${azimuth}°` : t(`cardinal.${cardinal.key}`) }
        : {})
    });
  }
  const shown = degrees(wrapDegrees(Math.round(heading)), language);
  return (
    <div
      className="card card--float tape"
      role="img"
      aria-label={t('ar.heading', { degrees: shown })}
    >
      {ticks.map((tick) => (
        <span
          key={tick.at}
          className="tape__tick"
          style={{ left: `${tick.at}%`, height: tick.major ? 12 : 6 }}
        />
      ))}
      {ticks
        .filter((tick) => tick.text !== undefined && Math.abs(tick.at - 50) > 8)
        .map((tick) => (
          <span key={`l${tick.at}`} className="tape__label" style={{ left: `${tick.at}%` }}>
            {tick.text}
          </span>
        ))}
      <span className="tape__needle">{shown}°</span>
    </div>
  );
}

function baseBasis(
  motion: Status,
  device: CameraBasis | undefined,
  look: { yaw: number; pitch: number }
): CameraBasis {
  return motion === 'on' && device !== undefined ? device : basisFromLook(look.yaw, look.pitch);
}

function viewportOf(
  element: HTMLElement,
  video: HTMLVideoElement | null,
  cameraOn: boolean
): Viewport {
  const width = element.clientWidth;
  const height = element.clientHeight;
  const imageWidth =
    cameraOn && video !== null && video.videoWidth > 0 ? video.videoWidth : Math.min(width, height);
  const imageHeight =
    cameraOn && video !== null && video.videoHeight > 0
      ? video.videoHeight
      : (Math.min(width, height) * 4) / 3;
  const portrait = height >= width;
  // Without a picture, the drawn sky uses the same lens held the way the screen is.
  const [w, h] = cameraOn || portrait ? [imageWidth, imageHeight] : [imageHeight, imageWidth];
  return { width, height, focal: coverFocal(width, height, w, h, LENS_LONG_SIDE_DEGREES) };
}

function frameOf(basis: CameraBasis, viewport: Viewport, props: ArProps): Frame {
  const heading = toDirection(basis.forward).azimuthDegrees;
  const place = (direction: SkyDirection | undefined) => {
    if (direction === undefined) return undefined;
    const p = project(direction, basis, viewport);
    const onScreen =
      p.inFront && p.x >= 0 && p.x <= viewport.width && p.y >= 0 && p.y <= viewport.height;
    const relative = ((direction.azimuthDegrees - heading + 540) % 360) - 180;
    const side: 'left' | 'right' = relative < 0 ? 'left' : 'right';
    const y = p.inFront ? Math.max(140, Math.min(viewport.height - 260, p.y)) : viewport.height / 2;
    return { x: p.x, y, onScreen, side };
  };
  const path = props.day.solar.path;
  return {
    heading,
    viewport,
    ...(props.sun.aboveHorizon ? { sun: place(props.sun)! } : {}),
    ...(path[0] === undefined ? {} : { rise: place(path[0])! }),
    ...(path.at(-1) === undefined ? {} : { set: place(path.at(-1))! })
  };
}

function edgeStyle(side: 'left' | 'right', y: number): CSSProperties {
  return side === 'left' ? { left: 8, top: y } : { right: 8, top: y };
}

function edgeText(side: 'left' | 'right', text: string): string {
  return side === 'left' ? `← ${text}` : `${text} →`;
}

function chipClass(quality: CompassQuality): string {
  if (quality === 'good') return 'chip-button chip-button--ok';
  if (quality === 'poor') return 'chip-button chip-button--warning';
  return 'chip-button';
}

function chipText(
  quality: CompassQuality,
  error: number | undefined,
  t: ReturnType<typeof useWords>['t']
): string {
  if (quality === 'poor') return t('compass.chipPoor');
  if (quality === 'unknown' || error === undefined) return t('compass.chipSettling');
  return t('compass.chip', { degrees: shownError(error) });
}
